import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { Capacitor } from "@capacitor/core";
import { useAuth } from "@/auth/AuthContext";
import { saveAuthenticatedDownload } from "@/native/files";
import { formatTimestamp } from "@/utils/datetime";
import { requestErrorMessage } from "@/lib/requestError";
import { newestFirst } from "@/utils/sortHistory";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/data-transfer/company-exports`;
const ACTIVE = ["queued", "capturing", "building"];
const READY = ["ready", "ready_with_warnings"];
const labels = { queued: "Queued", capturing: "Collecting records", building: "Preparing archive", ready: "Ready",
  ready_with_warnings: "Ready with warnings", failed: "Failed", canceled: "Canceled", expired: "Expired" };
const unavailable = {
  disabled: "Full company export has not been enabled for this environment.",
  private_storage_required: "Private file storage must be configured before full company exports can run.",
  snapshot_required: "The database is not ready to create a consistent company archive.",
  database_unavailable: "The export service could not reach the database. Try refreshing.",
};
const categoryLabel = value => String(value).replaceAll("_", " ");
function requestIdentity() {
  if (window.crypto.randomUUID) return window.crypto.randomUUID();
  const bytes = window.crypto.getRandomValues(new Uint8Array(16));
  bytes[6] = (bytes[6] & 15) | 64; bytes[8] = (bytes[8] & 63) | 128;
  const hex = Array.from(bytes, value => value.toString(16).padStart(2, "0")).join("");
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

export default function CompanyExportPanel() {
  const { user, hasPermission } = useAuth();
  const allowed = user?.role === "owner_admin" && Boolean(hasPermission?.("imports:manage"));
  const [capability, setCapability] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [cursor, setCursor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [confirming, setConfirming] = useState(false);
  const [now, setNow] = useState(Date.now);
  const requestId = useRef(null);
  const actionLock = useRef(false);
  const live = useRef(false);
  const generation = useRef(0);
  const refreshing = useRef(false);
  const native = Capacitor.isNativePlatform();
  const hasActive = jobs.some(job => ACTIVE.includes(job.state));
  const refresh = useCallback(async ({ more = false, before = null, quiet = false } = {}) => {
    if (!allowed || (quiet && refreshing.current)) return;
    refreshing.current = true;
    const current = ++generation.current;
    if (!quiet) setLoading(true);
    try {
      const [caps, response] = await Promise.all([
        quiet ? Promise.resolve({ data: null }) : axios.get(`${API}/capabilities`), axios.get(API, before ? { params: { before } } : undefined),
      ]);
      if (!live.current || current !== generation.current) return;
      if (caps.data) setCapability(caps.data);
      setError(""); setNow(Date.now());
      setJobs(existing => {
        const rows = response.data.jobs || [];
        if (quiet) return [...rows, ...existing.filter(item => !rows.some(row => row.id === item.id))];
        if (!more) return rows;
        return [...existing, ...rows.filter(row => !existing.some(item => item.id === row.id))];
      });
      if (!quiet) setCursor(response.data.next_cursor || null);
    } catch (failure) {
      if (!live.current || current !== generation.current) return;
      if ([401, 403].includes(failure.response?.status)) { setJobs([]); setCapability(null); }
      setError(requestErrorMessage(failure, "Full company exports could not be loaded. Please try again."));
    } finally {
      if (current === generation.current) { refreshing.current = false; if (live.current) setLoading(false); }
    }
  }, [allowed]);

  useEffect(() => {
    live.current = true; refresh();
    const requests = generation;
    return () => { live.current = false; requests.current++; };
  }, [refresh]);
  useEffect(() => {
    if (!hasActive) return undefined;
    const timer = window.setInterval(() => { if (document.visibilityState === "visible") refresh({ quiet: true }); }, 3000);
    return () => window.clearInterval(timer);
  }, [hasActive, refresh]);
  useEffect(() => {
    const next = Math.min(...jobs.filter(job => READY.includes(job.state)).map(job => Date.parse(job.expires_at)).filter(value => value > now));
    if (!Number.isFinite(next)) return undefined;
    const timer = window.setTimeout(() => setNow(Date.now()), Math.min(next - now + 50, 2147483647));
    return () => window.clearTimeout(timer);
  }, [jobs, now]);

  const create = async (retrying = false) => {
    if (actionLock.current || (!capability?.enabled && !retrying)) return;
    actionLock.current = true; setBusy("create"); setError(""); setMessage(""); setConfirming(false);
    try {
      requestId.current ||= requestIdentity();
      const response = await axios.post(API, { request_id: requestId.current });
      if (!live.current) return;
      requestId.current = null;
      setJobs(existing => [response.data, ...existing.filter(job => job.id !== response.data.id)]);
      setMessage("Company archive requested. Its status will update here.");
    } catch (failure) {
      if (live.current) setError(requestErrorMessage(failure, "The export request could not be confirmed. Retry the same request to check its status safely."));
    } finally { actionLock.current = false; if (live.current) setBusy(""); }
  };
  const cancel = async job => {
    if (actionLock.current) return;
    actionLock.current = true; setBusy(job.id); setError(""); setMessage("");
    try { await axios.delete(`${API}/${job.id}`); if (live.current) await refresh(); }
    catch (failure) { if (live.current) setError(requestErrorMessage(failure, "The export could not be canceled. Please refresh its status before trying again.")); }
    finally { actionLock.current = false; if (live.current) setBusy(""); }
  };
  const download = async job => {
    if (actionLock.current || native || !capability?.enabled) return;
    actionLock.current = true; setBusy(job.id); setError(""); setMessage("");
    try {
      const current = (await axios.get(`${API}/${job.id}`)).data;
      if (!live.current) return;
      if (!READY.includes(current.state) || !(Date.parse(current.expires_at) > Date.now())) {
        await refresh(); throw new Error("Archive is no longer available");
      }
      const delivery = await saveAuthenticatedDownload(`${API}/${job.id}/download`, `jfm-company-export-${job.id}.zip`, { openInNewTab: true });
      if (live.current && delivery.status !== "cancelled") setMessage("Download started. Your browser handles saving the archive.");
    } catch (failure) { if (live.current) setError(requestErrorMessage(failure, "The archive could not be downloaded. Refresh its status and try again.")); }
    finally { actionLock.current = false; if (live.current) setBusy(""); }
  };

  if (!allowed) return null;
  return <section className="data-card company-export-panel" aria-labelledby="company-export-heading">
    <h2 id="company-export-heading">Full company export</h2>
    <p>Create a private archive of the records and original attachments your current access permits. Excluded categories and unavailable files are reported with the archive.</p>
    {loading && !capability && <p role="status">Loading company exports…</p>}
    {capability && !capability.enabled && <p>{unavailable[capability.reason] || "Full company export is currently unavailable."}</p>}
    {capability?.retention_hours && <p>Finished archives remain available for {capability.retention_hours} hours.</p>}
    {capability?.exclusions?.length > 0 && <p>Excluded categories: {capability.exclusions.map(row => categoryLabel(row.category)).join(", ")}.</p>}
    <div className="company-export-actions">
      <Button type="button" disabled={!capability?.enabled || Boolean(busy) || hasActive || Boolean(requestId.current)} onClick={() => setConfirming(true)}>New company archive</Button>
      <Button type="button" variant="outline" disabled={loading || Boolean(busy)} onClick={() => refresh()}>Refresh exports</Button>
      {requestId.current && <><p>The previous archive request is unconfirmed. Retry it to safely check the same request.</p><Button type="button" disabled={Boolean(busy)} onClick={() => create(true)}>Retry request</Button></>}
    </div>
    {error && <p role="alert">{error}</p>}
    {message && <p role="status">{message}</p>}
    {native && <p>Open JFM in your browser to download full company archives.</p>}
    {!loading && !jobs.length && <p>No company archives have been requested by your account.</p>}
    <div className="company-export-history">
      {newestFirst(jobs).map(job => {
        const expired = job.state === "expired" || (READY.includes(job.state) && !(Date.parse(job.expires_at) > now));
        return <article className="company-export-job" key={job.id}>
          <h3>{expired ? "Expired" : labels[job.state] || "Status unavailable"}</h3>
          <p>Requested {formatTimestamp(job.created_at)}{job.expires_at && ` · Expires ${formatTimestamp(job.expires_at)}`}</p>
          {Object.keys(job.category_counts || {}).length > 0 && <p>{Object.entries(job.category_counts).map(([category, count]) => `${count} ${categoryLabel(category)}`).join(" · ")}</p>}
          {job.missing_count > 0 && <p>{job.missing_count} original files were unavailable.</p>}
          {job.relationship_warnings?.length > 0 && <p>The archive contains {job.relationship_warnings.length} relationship warnings for review.</p>}
          {job.error_message && <p>{job.error_message}</p>}
          <div className="company-export-actions">
            {ACTIVE.includes(job.state) && <Button type="button" variant="outline" disabled={Boolean(busy)} onClick={() => cancel(job)}>Cancel export</Button>}
            {!expired && READY.includes(job.state) && <Button type="button" disabled={Boolean(busy) || native || !capability?.enabled} onClick={() => download(job)}>Download archive</Button>}
          </div>
        </article>;
      })}
    </div>
    {cursor && <Button type="button" variant="outline" disabled={loading || Boolean(busy)} onClick={() => refresh({ more: true, before: cursor })}>Load older exports</Button>}
    <Dialog open={confirming} onOpenChange={setConfirming}>
      <DialogContent><DialogHeader><DialogTitle>Create company archive?</DialogTitle>
        <DialogDescription>This prepares a private archive containing company records and attachments allowed by your current access. Keep the downloaded file private.</DialogDescription>
      </DialogHeader><DialogFooter><Button autoFocus type="button" variant="outline" onClick={() => setConfirming(false)}>Cancel</Button>
        <Button type="button" disabled={Boolean(busy) || !capability?.enabled} onClick={() => create()}>Create archive</Button>
      </DialogFooter></DialogContent>
    </Dialog>
  </section>;
}
